import React from 'react';
import { Transition } from '@headlessui/react';
import { toast } from 'react-toastify';
import { fetchWithAuth } from '../utils/apiHelper';
import { Link } from 'react-router-dom';
// Heroicons - you can install them with "npm install @heroicons/react"
import { CurrencyDollarIcon, TrashIcon } from '@heroicons/react/20/solid';

const TrabajosList = ({ data, fetchData }) => {
  // 1) Destructure pagos & senas from data
  const { pagos = [], senas = [] } = data;

  // 2) Helper: Calculate NET amount for each pago (subtract seña, add recargo)
  const getNetAmount = (pago) => {
    let net = pago.monto;

    // Subtract seña
    if (pago.tiene_sena && pago.monto_sena) {
      net -= pago.monto_sena;
    }

    // Add recargo if any
    if (pago.tiene_recargo && pago.valor_recargo > 0) {
      net += net * (pago.valor_recargo / 100);
    }

    return net;
  };

  // 3) Calculate totals for pagos, using getNetAmount
  const calculatePaymentTotals = (pagosArray) => {
    const totals = {};
    pagosArray.forEach((pago) => {
      const method = pago.metodo_pago || 'Desconocido';
      const netAmount = getNetAmount(pago);

      if (!totals[method]) {
        totals[method] = 0;
      }
      totals[method] += netAmount;
    });
    return totals;
  };

  // 4) Calculate totals for senas
  const calculateSenaTotals = (senasArray) => {
    const totals = {};
    senasArray.forEach((sena) => {
      const method = sena.metodo_pago || 'Desconocido';
      if (!totals[method]) {
        totals[method] = 0;
      }
      totals[method] += sena.monto;
    });
    return totals;
  };

  // 5) Build dictionaries of totals
  const pagoTotals = calculatePaymentTotals(pagos);
  const senaTotals = calculateSenaTotals(senas);

  // 6) Summaries
  const totalPagos = Object.values(pagoTotals).reduce((acc, val) => acc + val, 0);
  const totalSenas = Object.values(senaTotals).reduce((acc, val) => acc + val, 0);
  const grandTotal = totalPagos + totalSenas;

  // 7) Handle delete (for pagos)
  const handleDelete = async (pagoId) => {
    try {
      await fetchWithAuth(`/operaciones/facturados/${pagoId}`, {
        method: 'DELETE',
      });
      toast.success('Registro eliminado exitosamente.');
      fetchData('facturados');
    } catch (error) {
      console.error('Error eliminando el registro:', error);
      toast.error('Error al eliminar el registro.');
    }
  };

  // 8) Show the summary block only if there's something to display
  const shouldShowTable = pagos.length > 0 || senas.length > 0;

  return (
    <Transition
      show={shouldShowTable}
      enter="transition-opacity duration-500"
      enterFrom="opacity-0"
      enterTo="opacity-100"
      leave="transition-opacity duration-500"
      leaveFrom="opacity-100"
      leaveTo="opacity-0"
    >
      <div className="p-4 md:p-10">
        {/* Main container with a gradient background */}
        <div className="bg-gradient-to-br from-white via-gray-50 to-gray-100 p-5 md:p-8 rounded-lg shadow-2xl">
          {/* Header with icon and title */}
          <div className="flex items-center mb-6 space-x-3">
            <CurrencyDollarIcon className="w-10 h-10 text-blue-600" />
            <h3 className="text-2xl md:text-3xl font-extrabold text-blue-800 tracking-tight">
              Resumen de Operaciones
            </h3>
          </div>

          {/* ---- PAGOS TABLE ---- */}
          {pagos.length > 0 && (
            <>
              <h4 className="text-xl font-bold text-green-700 mb-2 flex items-center space-x-2">
                <CurrencyDollarIcon className="w-5 h-5 text-green-600" />
                <span>Pagos Completos</span>
              </h4>
              <div className="overflow-x-auto mb-8">
                <table className="min-w-full bg-white border border-gray-200 rounded-lg shadow-md">
                  <thead className="bg-gray-200 border-b-2 border-gray-300">
                    <tr>
                      <th className="px-3 md:px-6 py-3 text-left text-xs md:text-sm font-bold text-gray-700 uppercase tracking-wider">
                        Tipo
                      </th>
                      <th className="px-3 md:px-6 py-3 text-left text-xs md:text-sm font-bold text-gray-700 uppercase tracking-wider">
                        Fecha Turno
                      </th>
                      <th className="px-3 md:px-6 py-3 text-left text-xs md:text-sm font-bold text-gray-700 uppercase tracking-wider">
                        Empleado
                      </th>
                      <th className="px-3 md:px-6 py-3 text-left text-xs md:text-sm font-bold text-gray-700 uppercase tracking-wider">
                        Cliente
                      </th>
                      <th className="px-3 md:px-6 py-3 text-left text-xs md:text-sm font-bold text-gray-700 uppercase tracking-wider">
                        Servicio
                      </th>
                      <th className="px-3 md:px-6 py-3 text-left text-xs md:text-sm font-bold text-gray-700 uppercase tracking-wider">
                        Monto
                      </th>
                      <th className="px-3 md:px-6 py-3 text-left text-xs md:text-sm font-bold text-gray-700 uppercase tracking-wider">
                        Método Pago
                      </th>
                      <th className="px-3 md:px-6 py-3 text-left text-xs md:text-sm font-bold text-gray-700 uppercase tracking-wider">
                        Seña
                      </th>
                      <th className="px-3 md:px-6 py-3 text-left text-xs md:text-sm font-bold text-gray-700 uppercase tracking-wider">
                        Acciones
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {pagos.map((pago) => {
                      const pagoId = pago.pago_id;
                      // netAmount includes recargo if any
                      const netAmount = getNetAmount(pago);

                      return (
                        <tr
                          key={pagoId}
                          className="hover:bg-green-50 transition-colors group"
                        >
                          <td className="px-3 md:px-6 py-4 font-medium text-gray-700">
                            <div className="flex items-center space-x-2">
                              <CurrencyDollarIcon className="w-5 h-5 text-green-600" />
                              <span className="text-green-800 bg-green-100 px-2 py-1 rounded text-xs font-semibold tracking-wide">
                                Pago Completo
                              </span>
                            </div>
                          </td>
                          <td className="px-3 md:px-6 py-4 text-gray-700">
                            {pago.turno_fecha
                              ? new Date(pago.turno_fecha).toLocaleDateString()
                              : 'N/A'}
                          </td>
                          <td className="px-3 md:px-6 py-4 text-gray-700">
                            {pago.empleado_nombre || 'N/A'}
                          </td>
                          <td className="px-3 md:px-6 py-4 text-gray-700">
                            {pago.cliente_id ? (
                              <Link
                                to={`/detalles/cliente/${pago.cliente_id}`}
                                className="text-indigo-500 hover:underline"
                              >
                                {pago.cliente_nombre} {pago.cliente_apellido}
                              </Link>
                            ) : (
                              'Cliente no registrado'
                            )}
                          </td>
                          <td className="px-3 md:px-6 py-4 text-gray-700">
                            {pago.servicio_nombre}
                          </td>
                          <td className="px-3 md:px-6 py-4 font-semibold text-gray-800">
                            {/* netAmount might have decimals if recargo is fraction. 
                                Round if you want (already using Math.round). */}
                            ${Math.round(netAmount).toLocaleString()}
                          </td>
                          <td className="px-3 md:px-6 py-4 text-gray-700">
                            {pago.metodo_pago}
                            {pago.tiene_recargo && pago.valor_recargo > 0 && (
                              <span className="ml-2 text-xs text-red-600 font-medium">
                                (+{pago.valor_recargo}% recargo)
                              </span>
                            )}
                          </td>
                          <td className="px-3 md:px-6 py-4">
                            {pago.tiene_sena && pago.monto_sena > 0 ? (
                              <span className="text-yellow-600 font-semibold">
                                ${pago.monto_sena.toLocaleString()}
                              </span>
                            ) : (
                              <span className="text-gray-400">N/A</span>
                            )}
                          </td>
                          <td className="px-3 md:px-6 py-4">
                            <button
                              onClick={() => handleDelete(pagoId)}
                              className="inline-flex items-center space-x-1 text-red-600 hover:text-red-800 focus:outline-none focus:ring-2 focus:ring-red-300 rounded"
                            >
                              <TrashIcon className="w-5 h-5" />
                              <span>Eliminar</span>
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </>
          )}

          {/* ---- SEÑAS TABLE ---- */}
          {senas.length > 0 && (
            <>
              <h4 className="text-xl font-bold text-yellow-700 mb-2 flex items-center space-x-2">
                <CurrencyDollarIcon className="w-5 h-5 text-yellow-600" />
                <span>Señas Registradas</span>
              </h4>
              <div className="overflow-x-auto mb-8">
                <table className="min-w-full bg-white border border-gray-200 rounded-lg shadow-md">
                  <thead className="bg-gray-200 border-b-2 border-gray-300">
                    <tr>
                      <th className="px-3 md:px-6 py-3 text-left text-xs md:text-sm font-bold text-gray-700 uppercase tracking-wider">
                        Tipo
                      </th>
                      <th className="px-3 md:px-6 py-3 text-left text-xs md:text-sm font-bold text-gray-700 uppercase tracking-wider">
                        Fecha Turno
                      </th>
                      <th className="px-3 md:px-6 py-3 text-left text-xs md:text-sm font-bold text-gray-700 uppercase tracking-wider">
                        Hora Turno
                      </th>
                      <th className="px-3 md:px-6 py-3 text-left text-xs md:text-sm font-bold text-gray-700 uppercase tracking-wider">
                        Registrado Por
                      </th>
                      <th className="px-3 md:px-6 py-3 text-left text-xs md:text-sm font-bold text-gray-700 uppercase tracking-wider">
                        Cliente
                      </th>
                      <th className="px-3 md:px-6 py-3 text-left text-xs md:text-sm font-bold text-gray-700 uppercase tracking-wider">
                        Servicio
                      </th>
                      <th className="px-3 md:px-6 py-3 text-left text-xs md:text-sm font-bold text-gray-700 uppercase tracking-wider">
                        Método Pago
                      </th>
                      <th className="px-3 md:px-6 py-3 text-left text-xs md:text-sm font-bold text-gray-700 uppercase tracking-wider">
                        Monto Seña
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {senas.map((sena) => {
                      const senaId = sena.sena_id;
                      return (
                        <tr
                          key={senaId}
                          className="bg-yellow-50 hover:bg-yellow-100 transition-colors group"
                        >
                          <td className="px-3 md:px-6 py-4 font-medium text-yellow-700">
                            <div className="flex items-center space-x-2">
                              <CurrencyDollarIcon className="w-5 h-5 text-yellow-600" />
                              <span className="bg-yellow-100 text-yellow-800 px-2 py-1 rounded text-xs font-semibold tracking-wide">
                                Solo Seña
                              </span>
                            </div>
                          </td>

                          {/* Fecha del turno */}
                          <td className="px-3 md:px-6 py-4 text-yellow-800 font-medium">
                            {sena.turno_fecha
                              ? new Date(sena.turno_fecha).toLocaleDateString()
                              : 'N/A'}
                          </td>

                          {/* Hora del turno */}
                          <td className="px-3 md:px-6 py-4 text-yellow-700">
                            {sena.turno_hora || 'N/A'}
                          </td>

                          {/* Usuario que registró la seña */}
                          <td className="px-3 md:px-6 py-4 text-yellow-700">
                            {sena.usuario_sena || 'N/A'}
                          </td>

                          {/* Cliente */}
                          <td className="px-3 md:px-6 py-4 text-yellow-700">
                            {sena.cliente_id ? (
                              <Link
                                to={`/detalles/cliente/${sena.cliente_id}`}
                                className="text-indigo-500 hover:underline"
                              >
                                {sena.cliente_nombre} {sena.cliente_apellido}
                              </Link>
                            ) : (
                              'Cliente no registrado'
                            )}
                          </td>

                          {/* Servicio */}
                          <td className="px-3 md:px-6 py-4 text-yellow-700">
                            {sena.servicio_nombre || 'N/A'}
                          </td>

                          {/* Método de pago */}
                          <td className="px-3 md:px-6 py-4 text-yellow-700">
                            {sena.metodo_pago}
                          </td>

                          {/* Monto de la Seña */}
                          <td className="px-3 md:px-6 py-4 text-yellow-800 font-semibold">
                            ${sena.monto.toLocaleString()}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </>
          )}

          {/* --- Resumen de Totales --- */}
          {(Object.keys(pagoTotals).length > 0 || Object.keys(senaTotals).length > 0) && (
            <div className="bg-white p-4 md:p-6 rounded-lg shadow-inner border border-gray-100">
              <h3 className="text-xl md:text-2xl font-bold mb-4 text-blue-800">
                Resumen de Totales
              </h3>

              {/* Totales de PAGOS */}
              {Object.keys(pagoTotals).length > 0 && (
                <div className="mb-6">
                  <h4 className="text-md md:text-lg font-semibold mb-2 text-gray-700 flex items-center space-x-2">
                    <CurrencyDollarIcon className="w-5 h-5 text-green-600" />
                    <span>Pagos</span>
                  </h4>
                  <ul className="space-y-2">
                    {Object.keys(pagoTotals).map((metodo) => (
                      <li
                        key={metodo}
                        className="flex justify-between items-center border-b py-2"
                      >
                        <span className="text-base font-medium text-gray-600">
                          {metodo}:
                        </span>
                        <span className="text-base font-semibold text-gray-900">
                          {Math.round(pagoTotals[metodo]).toLocaleString()}
                        </span>
                      </li>
                    ))}
                  </ul>
                  <div className="flex justify-between items-center mt-3 pt-3 border-t border-gray-200">
                    <span className="text-base font-bold text-gray-700">Total Pagos:</span>
                    <span className="text-base font-bold text-gray-900">
                      {Math.round(totalPagos).toLocaleString()}
                    </span>
                  </div>
                </div>
              )}

              {/* Totales de SEÑAS */}
              {Object.keys(senaTotals).length > 0 && (
                <div className="mb-4">
                  <h4 className="text-md md:text-lg font-semibold mb-2 text-gray-700 flex items-center space-x-2">
                    <CurrencyDollarIcon className="w-5 h-5 text-yellow-600" />
                    <span>Señas</span>
                  </h4>
                  <ul className="space-y-2">
                    {Object.keys(senaTotals).map((metodo) => (
                      <li
                        key={metodo}
                        className="flex justify-between items-center border-b py-2"
                      >
                        <span className="text-base font-medium text-gray-600">
                          {metodo}:
                        </span>
                        <span className="text-base font-semibold text-gray-900">
                          {senaTotals[metodo].toLocaleString()}
                        </span>
                      </li>
                    ))}
                  </ul>
                  <div className="flex justify-between items-center mt-3 pt-3 border-t border-gray-200">
                    <span className="text-base font-bold text-gray-700">Total Señas:</span>
                    <span className="text-base font-bold text-gray-900">
                      {totalSenas.toLocaleString()}
                    </span>
                  </div>
                </div>
              )}

              {/* Gran Total */}
              {(totalPagos > 0 || totalSenas > 0) && (
                <div className="flex justify-between items-center mt-4 pt-4 border-t-2 border-gray-300">
                  <span className="text-lg font-extrabold text-gray-800">Gran Total:</span>
                  <span className="text-lg font-extrabold text-gray-900">
                    {Math.round(grandTotal).toLocaleString()}
                  </span>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </Transition>
  );
};

export default TrabajosList;
